import { SELECTION_ANY } from '../../../components/bearing-search/api/api';
import { SealDataKey, SealSpecificationKey, SealSpecificationPayload, TimkenUnitDimension, UnitType } from '../../../components/bearing-search/types/types';
import { sealSpecsCategoryData, sealSpecsLabels, sealSpecsRequiredKeys, text } from '../../data';
import { SealCategory } from '../../types/types';
import { getTooltip, handleActionError } from '../../utils/engineering-tools';
import { convertNumberInput, isValidBoundary } from '../../utils/validations';
import { getDimensionOptions } from './options';

export type SpecificationSearchState = {
	category: SealCategory,
	unitDimension: TimkenUnitDimension,
	material?: string,
	type?: string,
	specsFilters: SealSpecsFilterFilterMap,
	crossSectionHeight?: number,
	overallWidth?: number,
	sortExpression?: SealDataKey,
	validation: SpecificationSearchValidationMap,
	requestData?: SealSpecificationPayload,
};

export type SealSpecsFilter = {
	id: string;
	label: string;
	value?: number;
	type: UnitType;
	tooltip?: string;
	isApplicable?: boolean;
};

export type SpecificationSearchInputKey = keyof Pick<SealSpecificationPayload, 'crossSectionHeight' | 'overallWidth' | 'shaftDiameterMin' | 'shaftDiameterMax' | 'housingBoreMin' | 'housingBoreMax' | 'outsideDiameterMin' | 'outsideDiameterMax' | 'widthMin' | 'widthMax'>;
type SealSpecsFilterFilterMap = Record<SpecificationSearchInputKey, SealSpecsFilter>;
type SpecificationSearchValidationMap = Map<SpecificationSearchInputKey, { invalid: boolean, message: string; }>;

const defaultFilterValue: Pick<SealSpecsFilter, 'value' | 'type' | 'isApplicable'> = {
	type: 'length',
	isApplicable: true,
};

const initialSpecsFilters: SealSpecsFilterFilterMap = {
	shaftDiameterMin: {
		...defaultFilterValue,
		id: 'seal_specs_shaft_diameter_min',
		label: sealSpecsLabels.shaftDiameterMin || '',
		tooltip: sealSpecsLabels.shaftDiameterMin && getTooltip( sealSpecsLabels.shaftDiameterMin ),
	},
	shaftDiameterMax: {
		...defaultFilterValue,
		id: 'seal_specs_shaft_diameter_max',
		label: sealSpecsLabels.shaftDiameterMax || '',
	},
	housingBoreMin: {
		...defaultFilterValue,
		id: 'seal_specs_housing_bore_min',
		label: sealSpecsLabels.housingBoreMin || '',
	},
	housingBoreMax: {
		...defaultFilterValue,
		id: 'seal_specs_housing_bore_max',
		label: sealSpecsLabels.housingBoreMax || '',
	},
	outsideDiameterMin: {
		...defaultFilterValue,
		id: 'seal_specs_outside_diameter_min',
		label: sealSpecsLabels.outsideDiameterMin || '',
	},
	outsideDiameterMax: {
		...defaultFilterValue,
		id: 'seal_specs_outside_diameter_max',
		label: sealSpecsLabels.outsideDiameterMax || '',
	},
	widthMin: {
		...defaultFilterValue,
		id: 'seal_specs_width_min',
		label: sealSpecsLabels.widthMin || '',
	},
	widthMax: {
		...defaultFilterValue,
		id: 'seal_specs_width_max',
		label: sealSpecsLabels.widthMax || '',
	},
	overallWidth: {
		...defaultFilterValue,
		id: 'seal_specs_overall_width',
		label: sealSpecsLabels.overallWidth || '',
		isApplicable: false,
	},
	crossSectionHeight: {
		...defaultFilterValue,
		id: 'seal_specs_cross_section_height',
		label: sealSpecsLabels.crossSectionHeight || '',
		isApplicable: false,
	},
};

const filterPairs: Array<[SpecificationSearchInputKey, SpecificationSearchInputKey]> = [
	[ 'shaftDiameterMin', 'shaftDiameterMax' ],
	[ 'housingBoreMin', 'housingBoreMax' ],
	[ 'outsideDiameterMin', 'outsideDiameterMax' ],
	[ 'widthMin', 'widthMax' ],
];

export const isSealKeyApplicable = (
	category: SealCategory,
	key: SealSpecificationKey,
): boolean => {
	return sealSpecsCategoryData[ category ].requestFields.includes( key );
};

const validationConfig = {
	message: '',
	invalid: false,
};

const getValidationMap = ( state?: SpecificationSearchState ): SpecificationSearchValidationMap => {
	if ( ! state ) {
		// assign initial value
		const initialValidationMap: SpecificationSearchValidationMap = new Map();

		Object.keys( initialSpecsFilters ).forEach( ( key ) => {
			initialValidationMap.set( key as SpecificationSearchInputKey, validationConfig );
		} );

		return initialValidationMap;
	}

	const { category, validation: map } = state;
	const validation = new Map( map );

	for ( const [ min, max ] of filterPairs ) {
		// Set valid if fields are hidden

		if ( ! isSealKeyApplicable( category, min ) ) {
			validation.set( min, validationConfig );
			validation.set( max, validationConfig );

			continue;
		}

		const minValue = state.specsFilters[ min ].value;
		const maxValue = state.specsFilters[ max ].value;

		const invalidBoundary = ! isValidBoundary( minValue, maxValue );

		if ( sealSpecsRequiredKeys.includes( min ) && minValue === undefined ) {
			validation.set( min, {
				invalid: true,
				message: text.errorRequired,
			} );
		} else {
			validation.set( min, {
				invalid: invalidBoundary,
				message: text.errorBoundaryLower,
			} );
		}

		if ( sealSpecsRequiredKeys.includes( max ) && maxValue === undefined ) {
			validation.set( max, {
				invalid: true,
				message: text.errorRequired,
			} );
		} else {
			validation.set( max, {
				invalid: invalidBoundary,
				message: text.errorBoundaryUpper,
			} );
		}
	}

	validation.set( 'crossSectionHeight', {
		invalid: sealSpecsRequiredKeys.includes( 'crossSectionHeight' ) && state.specsFilters.crossSectionHeight.value === undefined,
		message: text.errorRequired,
	} );

	validation.set( 'overallWidth', {
		invalid: sealSpecsRequiredKeys.includes( 'overallWidth' ) && state.specsFilters.overallWidth.value === undefined,
		message: text.errorRequired,
	} );

	return validation;
};

export const initialSpecificationState: SpecificationSearchState = {
	category: SealCategory.OIL_SEAL,
	unitDimension: 'Metric',
	material: SELECTION_ANY,
	type: SELECTION_ANY,
	specsFilters: initialSpecsFilters,
	validation: getValidationMap(),
};

export type SpecificationSearchAction =
	| { type: 'change_unit'; payload: TimkenUnitDimension; }
	| { type: 'change_seal_category'; payload: string; }
	| { type: 'change_number_field'; payload: [SpecificationSearchInputKey, unknown]; }
	| { type: 'change_material'; payload?: string; }
	| { type: 'change_type'; payload?: string; }
	| { type: 'change_sort_key'; payload: SealDataKey; }
	| { type: 'update_request_data'; }
	| { type: 'reset'; payload?: SpecificationSearchState; };

const getSealSpecsFormData = ( state: SpecificationSearchState ): SealSpecificationPayload | undefined => {
	const payload: Partial<SealSpecificationPayload> = {};
	const requestKeysOfCategory = sealSpecsCategoryData[ state.category ].requestFields;

	for ( const key of requestKeysOfCategory ) {
		if ( state[ key ] !== undefined ) {
			payload[ key as string ] = state[ key ];
		}

		if ( state.specsFilters[ key ] ) {
			payload[ key as string ] = state.specsFilters[ key ].value;
		}
	}

	if ( payload.material === SELECTION_ANY ) {
		payload.material = undefined;
	}

	if ( payload.type === SELECTION_ANY ) {
		payload.type = undefined;
	}

	// Add sort
	if ( state.sortExpression ) {
		payload.sortExpression = state.sortExpression;
		payload.sortDirection = 'desc';
	}

	return payload as SealSpecificationPayload;
};

const specificationReducer: React.Reducer<
	SpecificationSearchState,
	SpecificationSearchAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			return {
				...state,
				unitDimension: action.payload,
			};
		}
		case 'change_seal_category': {
			const newCategory = action.payload as SealCategory;
			const isMetricSupported = getDimensionOptions( newCategory ).some( ( dimension ) => dimension.value === 'Metric' );
			const newUnit: TimkenUnitDimension = isMetricSupported ? 'Metric' : 'Inch';

			const filters = { ...state.specsFilters };

			Object.keys( filters ).forEach( ( key ) => {
				filters[ key ].isApplicable = isSealKeyApplicable( newCategory, key as SealSpecificationKey );
			} );

			return {
				...state,
				unitDimension: newUnit,
				category: newCategory,
				specsFilters: filters,
				material: SELECTION_ANY,
				type: SELECTION_ANY,
			};
		}
		case 'change_material': {
			return {
				...state,
				material: action.payload,
			};
		}
		case 'change_type': {
			return {
				...state,
				type: action.payload,
			};
		}
		case 'change_number_field': {
			const [ key, value ] = action.payload;
			const { specsFilters } = state;

			if ( ! specsFilters[ key ] ) {
				return { ...state };
			}

			return {
				...state,
				specsFilters: {
					...specsFilters,
					[ key ]: {
						...specsFilters[ key ],
						value: convertNumberInput( value ),
					},
				},
			};
		}
		case 'change_sort_key': {
			return {
				...state,
				sortExpression: action.payload,
			};
		}
		case 'update_request_data': {
			const validation = getValidationMap( state );
			const isInValid = Array.from( validation.values() ).some( ( field ) => field.invalid );

			return {
				...state,
				validation,
				requestData: isInValid ? undefined : getSealSpecsFormData( state ),
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialSpecificationState;

			return {
				...newState,
			};
		}
	}

	return handleActionError( action );
};

export default specificationReducer;
